/**
 * Copy one short string to the clipboard, and report whether it worked so the caller can show
 * (or withhold) a confirmation.
 *
 * `navigator.clipboard` only exists in a secure context - https, or localhost while developing.
 * The hidden-textarea path keeps the copy working everywhere else: an http preview, an in-app
 * browser, an older Android WebView. Neither path throws; a `false` return means neither worked.
 */
export async function copyToClipboard(text) {
  const value = String(text == null ? '' : text);
  if (!value) return false;

  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch (error) {
    // Permission denied or an insecure context lying about itself - fall through to execCommand.
  }

  try {
    const field = document.createElement('textarea');
    field.value = value;
    field.setAttribute('readonly', '');
    // Off-screen but still focusable. `position: fixed` avoids scrolling the page on iOS.
    field.style.position = 'fixed';
    field.style.top = '0';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    field.setSelectionRange(0, value.length);
    const copied = document.execCommand('copy');
    document.body.removeChild(field);
    return copied;
  } catch (error) {
    return false;
  }
}
